import { Alert, Box, Button, Typography, useTheme } from '@mui/material';
import { SyntheticEvent } from 'react';
import { useBoolean } from 'usehooks-ts';

import { updateInfoAccuracy } from '@/api/user';

import { ActionAccordion } from '@/components/Form/ActionList/ActionAccordion';
import { Icon } from '@/components/Icon/Icon';
import { FullPageLoaderOverlay } from '@/components/Layout/FullPageLoader';

import { useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import { DocumentStatus } from '@/types/document';

import { getAPIErrorMessage } from '@/utils/api';

type WarrantyOfAccuracyAccordionProps = {
  disabled?: boolean;
};

export function WarrantyOfAccuracyAccordion({
  disabled,
}: Readonly<WarrantyOfAccuracyAccordionProps>) {
  const { t } = useTranslate();

  const user = useAppSelector(selectLoggedInUser);
  const theme = useTheme();
  const { value: isExpanded, setValue: setExpanded } = useBoolean(false);
  const isConfirmed = !!user?.infoAccuracyConfirmed;
  const { mutate, isPending, error, reset } = useMutationWithUserData({
    mutationFn: updateInfoAccuracy,
  });

  const handleAccordionExpand = (_event: SyntheticEvent, expanded: boolean) => {
    setExpanded(expanded);
  };

  const onConfirm = () => {
    reset();
    mutate({
      infoAccuracyConfirmed: true,
      operationId: user?.operationId,
    });
  };

  return (
    <>
      {isPending && <FullPageLoaderOverlay />}
      <ActionAccordion
        title={t('moreInfo.form.warranty.title')}
        status={isConfirmed ? DocumentStatus.Approved : undefined}
        expanded={isConfirmed ? false : isExpanded}
        onChange={handleAccordionExpand}
        disabled={disabled}
      >
        <Typography variant="body2" color={theme.palette.text.primary}>
          {t('moreInfo.form.warranty.body')}
        </Typography>
        {error && (
          <Box my={1.5}>
            <Alert
              icon={<Icon name="alert" />}
              color="error"
              variant="standard"
            >
              {getAPIErrorMessage(error)}
            </Alert>
          </Box>
        )}
        <Box textAlign="right" mt={0.5}>
          <Button onClick={onConfirm} disabled={disabled}>
            {t('moreInfo.form.warranty.button.confirm')}
          </Button>
        </Box>
      </ActionAccordion>
    </>
  );
}
